import * as React from 'react';
import classnames from 'classnames';

interface MultiRangeSliderProps {
    min: number;
    max: number;
    labelPrefix: string;
    labelSuffix: string;
    onChange: ({ min, max }: { min: number; max: number }) => void;
}

interface MultiRangeSliderState {
    minVal: number;
    maxVal: number;
}

class MultiRangeSlider extends React.Component<MultiRangeSliderProps, MultiRangeSliderState> {
    private minValRef: React.RefObject<HTMLInputElement>;
    private maxValRef: React.RefObject<HTMLInputElement>;
    private range: React.RefObject<HTMLDivElement>;
    private debounceTimer: number | null = null;

    constructor(props: MultiRangeSliderProps) {
        super(props);
        this.state = {
            minVal: props.min,
            maxVal: props.max,
        };
        this.minValRef = React.createRef();
        this.maxValRef = React.createRef();
        this.range = React.createRef();
    }

    getPercent(value: number) {
        const { min, max } = this.props;
        return Math.round(((value - min) / (max - min)) * 100);
    }

    componentDidUpdate(prevProps: MultiRangeSliderProps, prevState: MultiRangeSliderState) {
        if (this.range.current) {
            const minPercent = this.getPercent(this.state.minVal);
            const maxPercent = this.getPercent(this.state.maxVal);
            this.range.current.style.left = `${minPercent}%`;
            this.range.current.style.width = `${maxPercent - minPercent}%`;
        }
        if (prevState.minVal !== this.state.minVal || prevState.maxVal !== this.state.maxVal) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            this.debounceTimer = window.setTimeout(() => {
                this.props.onChange({ min: this.state.minVal, max: this.state.maxVal });
                this.debounceTimer = null;
            }, 200);
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }

    handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.min(Number(event.target.value), this.state.maxVal - 1);
        this.setState({ minVal: value });
        event.target.value = value.toString();
    };

    handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.max(Number(event.target.value), this.state.minVal + 1);
        this.setState({ maxVal: value });
        event.target.value = value.toString();
    };

    numberWithDots = (x: number) => {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    render() {
        const { min, max, labelPrefix, labelSuffix } = this.props;
        const { minVal, maxVal } = this.state;
        return (
            <div className="MultiRangeSliderContainer">
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={minVal}
                    ref={this.minValRef}
                    onChange={this.handleMinChange}
                    className={classnames('thumb thumb--zindex-3', {
                        'thumb--zindex-5': minVal > max - 100,
                    })}
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={maxVal}
                    ref={this.maxValRef}
                    onChange={this.handleMaxChange}
                    className="thumb thumb--zindex-4"
                />
                <div className="slider">
                    <div className="slider__track" />
                    <div ref={this.range} className="slider__range" />
                    <div className="slider__left-value">
                        {labelPrefix}{this.numberWithDots(minVal || 0)} {labelSuffix}
                    </div>
                    <div className="slider__right-value">
                        {labelPrefix}{this.numberWithDots(maxVal || 0)} {labelSuffix}
                    </div>
                </div>
            </div>
        );
    }
}

export default MultiRangeSlider;



// WEBPACK FOOTER //
// ./react/src/components/MultiRangeSlider.tsx